import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare, faShieldXmark, faXmark } from "@abodeminehq/parcel-ui/icons/fa/pro-solid";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  VStack,
  Logo,
} from "@abodeminehq/parcel-ui";
import { createServerFn } from "@tanstack/react-start";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { SIGNOUT_RETURN_KEY, useAuthContext } from "~/contexts/auth-context";
import { logtoConfig } from "~/lib/logto/logto";
import { getMcpConfig } from "~/lib/mcp/config";
import { verifyState } from "~/lib/mcp/crypto";
import type { McpAuthErrorDetails } from "~/lib/mcp/error-page";

interface DisplayDetails {
  reason: string;
  code?: string;
  clientLabel?: string | null;
  userName?: string;
  userEmail?: string;
  orgName?: string;
  onboardingUrl?: string;
}

const GENERIC: DisplayDetails = {
  reason:
    "Unable to complete MCP authentication. Please reconnect from your MCP client to try again.",
};

/**
 * Failure codes that no account switch can fix: org configuration, an
 * unsupported client, or an expired transaction. For these, signing out
 * just bounces the user back to the same screen — say so instead of
 * presenting sign-out as the remedy.
 */
const NOT_ACCOUNT_FIXABLE = new Set([
  "mcp_not_enabled",
  "organization_not_found",
  "missing_organization",
  "unsupported_client",
  "session_expired",
  "mcp_legal_not_accepted",
]);

function helpText(code: string | undefined): string | null {
  switch (code) {
    case "organization_not_found":
    case "missing_organization":
      return "Check the MCP server URL configured in your client — the organization in the URL does not exist.";
    case "unsupported_client":
      return "Connect from an approved MCP client (Claude, ChatGPT, Gemini, Cursor, VS Code, and similar).";
    case "mcp_legal_not_accepted":
      return "Review and accept the MCP terms in the AbodeMine dashboard, then reconnect your MCP client.";
    default:
      return null;
  }
}

// Verify the HMAC-signed details blob server-side so the page can only ever
// display failure text produced by our own auth endpoints.
const loadErrorDetails = createServerFn({ method: "POST" })
  .validator(z.object({ blob: z.string() }))
  .handler(async ({ data }): Promise<DisplayDetails> => {
    const config = getMcpConfig();
    try {
      const details = await verifyState<McpAuthErrorDetails>(data.blob, config.mcpStateSecret);
      if (details.exp < Date.now()) return GENERIC;
      return {
        reason: details.reason,
        code: details.code,
        clientLabel: details.clientLabel ?? null,
        userName: details.userName,
        userEmail: details.userEmail,
        orgName: details.orgName,
        onboardingUrl: details.onboardingUrl,
      };
    } catch {
      return GENERIC;
    }
  });

export const Route = createFileRoute("/mcp/auth/error")({
  component: McpAuthErrorPage,
});

function McpBrandHeader() {
  return (
    <div className="flex items-center justify-center gap-3">
      <Logo size="lg" />
      <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
      <img src="/logos/mcp.webp" alt="MCP" className="h-8 w-auto" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words font-medium">{value}</div>
    </div>
  );
}

function McpAuthErrorPage() {
  const [details, setDetails] = useState<DisplayDetails | null>(null);
  const [signingOut, setSigningOut] = useState(false);
  const { signOut } = useAuthContext();

  async function handleSignOut() {
    setSigningOut(true);
    // /callback/signout honors this key and forwards to the MCP signed-out page.
    sessionStorage.setItem(SIGNOUT_RETURN_KEY, "/mcp/auth/signed-out");
    try {
      // SDK path: clears local SPA tokens, then redirects to Logto end-session.
      await signOut("/mcp/auth/signed-out");
    } catch (err) {
      // The SDK rejects when there is no local sign-in state (common here:
      // the MCP flow authenticates via the Logto session cookie, not the SPA).
      // RP-initiated logout directly — it only needs client_id and the
      // registered post-logout redirect URI, no local state.
      console.warn("[mcp-error] SDK signOut failed, using direct end-session", err);
      const endSession = new URL(`${logtoConfig.endpoint.replace(/\/+$/, "")}/oidc/session/end`);
      endSession.searchParams.set("client_id", logtoConfig.appId);
      endSession.searchParams.set(
        "post_logout_redirect_uri",
        new URL("/callback/signout", window.location.origin).toString(),
      );
      window.location.href = endSession.toString();
    }
  }

  useEffect(() => {
    const blob = new URLSearchParams(window.location.search).get("d");
    if (!blob) {
      setDetails(GENERIC);
      return;
    }
    loadErrorDetails({ data: { blob } })
      .then(setDetails)
      .catch(() => setDetails(GENERIC));
  }, []);

  const clientLabel = details?.clientLabel ?? "your MCP client";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2">
            <McpBrandHeader />
          </div>
          <div className="mx-auto mb-2">
            <FontAwesomeIcon icon={faShieldXmark} className="h-10 w-10 text-destructive" />
          </div>
          <CardTitle>Authentication Failed</CardTitle>
          <CardDescription>Unable to complete MCP authentication.</CardDescription>
        </CardHeader>
        <CardContent>
          {!details ? (
            <VStack spacing={4}>
              <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-foreground" />
            </VStack>
          ) : (
            <VStack spacing={4} className="items-stretch">
              <DetailRow label="User" value={details.userName} />
              <DetailRow label="Email" value={details.userEmail} />
              <DetailRow label="Organization" value={details.orgName} />
              {details.clientLabel && <DetailRow label="MCP Client" value={details.clientLabel} />}

              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                <p className="text-sm text-destructive">{details.reason}</p>
              </div>

              {helpText(details.code) && (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-sm text-muted-foreground">{helpText(details.code)}</p>
                </div>
              )}

              {details.onboardingUrl && (
                <Button className="w-full" asChild>
                  <a href={details.onboardingUrl}>
                    Open MCP onboarding
                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ml-1.5 h-4 w-4" />
                  </a>
                </Button>
              )}

              <Button
                className="w-full"
                variant={
                  details.onboardingUrl || NOT_ACCOUNT_FIXABLE.has(details.code ?? "")
                    ? "outline"
                    : "default"
                }
                disabled={signingOut}
                onClick={() => void handleSignOut()}
              >
                {signingOut ? "Signing out..." : "Sign out and try a different account"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {NOT_ACCOUNT_FIXABLE.has(details.code ?? "")
                  ? `Once resolved, reconnect the MCP server in ${clientLabel} to try again.`
                  : `After signing out, reconnect the MCP server in ${clientLabel} to try again with a different account.`}
              </p>
            </VStack>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
